import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, RefreshCw, Store, TriangleAlert } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsCompact } from '@/hooks/use-media-query';
import {
  formatCalendarDate,
  formatCurrency,
  formatNumber,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import { SectionTitle, StatGroup, StatTile } from '@/components/ui/stat-tile';
import { useAuthStore } from '@/features/auth/auth.store';
import {
  dailyColumns,
  groupingColumns,
  lossColumns,
  paymentColumns,
  productColumns,
} from './dashboard-columns';
import type { DashboardStat, Grouping } from './dashboard.types';
import { useDashboardQuery, useGroupingQuery } from './dashboard.api';
import { DashboardQuickView } from './dashboard-quick-view';
import { GrowthCaption } from './growth';
import { RevenueLineChart } from './revenue-line-chart';
import { ShareBar } from './share-bar';
import { SortableTable } from '@/components/ui/sortable-table';
import { TopProductsDetail } from './top-products-detail';
import { StoreMultiSelect } from './store-multi-select';

const firstOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

const GROUPINGS: Grouping[] = ['division', 'productGroup', 'department', 'category'];
const GROUPING_LABEL_KEY: Record<Grouping, string> = {
  division: 'dashboard.division',
  productGroup: 'dashboard.productGroup',
  department: 'dashboard.department',
  category: 'dashboard.category',
};

export function DashboardView({ scope }: { scope: 'global' | 'store' }) {
  const { t, i18n } = useTranslation();
  const isCompact = useIsCompact();
  const business = useAuthStore((s) => s.business);

  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(today);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [paymentView, setPaymentView] = useState<'tenderTypes' | 'cardTypes'>('tenderTypes');
  const [grouping, setGrouping] = useState<Grouping>('category');
  const [dailyOpen, setDailyOpen] = useState(false);

  const query = useMemo(
    () => ({
      startDate: start,
      endDate: end,
      // An empty list means every store, so the store dashboard has to name its
      // own rather than leave the filter off.
      storeIds: scope === 'global' ? storeIds : business ? [business.id] : [],
    }),
    [start, end, storeIds, scope, business]
  );

  const { data, isPending, isError, isFetching, refetch } = useDashboardQuery(query);
  // Skeletons are for a first load only. A refresh keeps the numbers on screen
  // and dims them — replacing a readable figure with a grey box to say "the
  // same figure is on its way" loses more than it tells you.
  const loading = isPending || !data;

  // "Where it sold" is its own request per grouping — the endpoint answers for
  // one at a time and is the most expensive of the set.
  const {
    data: groupingRows,
    isPending: groupingPending,
    isFetching: groupingFetching,
    refetch: refetchGrouping,
  } = useGroupingQuery(query, grouping, i18n.language);

  /**
   * What the breakdown adds up to.
   *
   * The section had no total, so the only whole-estate figure on this page to
   * compare a line-level report against was Gross Sales — which is rebuilt
   * from order headers and is a different measure entirely. This one is
   * `price × amount` over regular lines and equals Movement's *Merchandise*
   * exactly for the same dates and stores, which makes the two pages
   * reconcilable at a glance instead of by arithmetic.
   */
  const groupingTotal = (groupingRows ?? []).reduce(
    (sum, row) => sum + row.salesTotal.value,
    0
  );

  // The date range and the store picker feed both queries, so they stay locked
  // until both have settled; the grouping tabs only drive their own.
  const busy = isFetching || groupingFetching;
  /** Keeps a chart readable while its numbers are being replaced. */
  const stale = (fetching: boolean, pending: boolean) =>
    cn(fetching && !pending && 'opacity-60 transition-opacity');

  // The route is reachable without a store picked (a typed URL, a stale tab
  // after switching to Global) — nothing here means anything without one.
  if (scope === 'store' && !business) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('dashboard.storeTitle')} />
        <Card className="border-dashed shadow-none">
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full">
              <Store className="size-6" />
            </span>
            <div className="space-y-1">
              <p className="font-medium">{t('dashboard.noStoreSelectedTitle')}</p>
              <p className="text-muted-foreground max-w-sm text-sm">
                {t('dashboard.noStoreSelectedBody')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Every section draws on the same batch of requests, so a failure is the
  // whole page rather than an empty table in one of them. The likeliest cause
  // is the endpoints' role gate, which is narrower than this route's.
  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={scope === 'global' ? t('dashboard.globalTitle') : business!.name}
        />
        <Card className="border-dashed shadow-none">
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full">
              <TriangleAlert className="size-6" />
            </span>
            <div className="space-y-1">
              <p className="font-medium">{t('dashboard.errorTitle')}</p>
              <p className="text-muted-foreground max-w-sm text-sm">
                {t('dashboard.errorBody')}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="size-4" />
              {t('dashboard.refresh')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filterFields: FilterField[] = [
    {
      id: 'from',
      label: t('dashboard.from'),
      type: 'date',
      value: start,
      onChange: setStart,
      // Any date is a narrowing, so neither date should badge the button.
      resetValue: start,
    },
    {
      id: 'to',
      label: t('dashboard.to'),
      type: 'date',
      value: end,
      onChange: setEnd,
      resetValue: end,
    },
    ...(scope === 'global'
      ? [
          {
            id: 'stores',
            label: t('dashboard.stores'),
            // The drawer stages values as strings, so the selection travels as
            // one and is split back out on the way in.
            value: storeIds.join(','),
            onChange: (value: string) =>
              setStoreIds(value ? value.split(',') : []),
            // Empty means every store, which is not a filter.
            resetValue: '',
            render: ({ id, value, onChange, disabled }) => (
              <StoreMultiSelect
                id={id}
                selected={value ? value.split(',') : []}
                onChange={(ids) => onChange(ids.join(','))}
                disabled={disabled}
              />
            ),
          } satisfies FilterField,
        ]
      : []),
  ];

  const previousRangeLabel = data
    ? `${formatCalendarDate(data.previousStartDate)} – ${formatCalendarDate(data.previousEndDate)}`
    : '';

  /** A revenue tile's delta line — same "Trends" figure the old page kept in
   * a separate tab, folded straight into the tile it belongs to. */
  function revenueSub(stat: DashboardStat | undefined, isMoney: boolean) {
    return stat && <GrowthCaption stat={stat} isMoney={isMoney} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={scope === 'global' ? t('dashboard.globalTitle') : business!.name}
        description={
          scope === 'global'
            ? t('dashboard.globalDescription')
            : t('dashboard.storeDescription')
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              void refetch();
              void refetchGrouping();
            }}
          >
            <RefreshCw className={cn('size-4', busy && 'animate-spin')} />
            {t('dashboard.refresh')}
          </Button>
        }
      />

      <FilterBar
        busy={busy}
        fields={filterFields}
        summary={
          data && (
            <p className="text-muted-foreground text-sm">
              {t('dashboard.comparedTo')}{' '}
              <b className="text-foreground">{previousRangeLabel}</b>
            </p>
          )
        }
      />

      <StatGroup title={t('dashboard.revenue')}>
        {/* First, because it is the only tile here computed from the order
            lines rather than the order headers — so it is the one that ties to
            "Where it sold" below and to the Movement report, and the one to
            reach for when two pages seem to disagree. */}
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          tone="cash"
          label={t('dashboard.merchandise')}
          value={formatCurrency(data?.sales.merchandise.value)}
          tooltip={t('dashboard.merchandiseHelp')}
          sub={revenueSub(data?.sales.merchandise, true)}
        />
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          label={t('dashboard.grossSales')}
          value={formatCurrency(data?.sales.grossSales.value)}
          tooltip={t('dashboard.grossSalesHelp')}
          sub={revenueSub(data?.sales.grossSales, true)}
        />
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          tone="primary"
          label={t('dashboard.netSales')}
          value={formatCurrency(data?.sales.netSales.value)}
          tooltip={t('dashboard.netSalesHelp')}
          sub={revenueSub(data?.sales.netSales, true)}
        />
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          tone="good"
          label={t('dashboard.amountCollected')}
          value={formatCurrency(data?.sales.amountCollected.value)}
          tooltip={t('dashboard.amountCollectedHelp')}
          sub={revenueSub(data?.sales.amountCollected, true)}
        />
        {/* Beside Collected on purpose: it is the money side of a sale that
            Collected leaves out, while the goods it paid for are in
            Merchandise. */}
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          label={t('dashboard.giftCardRedemptions')}
          value={formatCurrency(data?.sales.giftCardRedemptions.value)}
          tooltip={t('dashboard.giftCardRedemptionsHelp')}
          sub={revenueSub(data?.sales.giftCardRedemptions, true)}
        />
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          label={t('dashboard.orders')}
          value={formatNumber(data?.sales.orders.value)}
          tooltip={t('dashboard.ordersHelp')}
          sub={revenueSub(data?.sales.orders, false)}
        />
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          label={t('dashboard.averageTicket')}
          value={formatCurrency(data?.sales.averageTicket.value)}
          tooltip={t('dashboard.averageTicketHelp')}
          sub={revenueSub(data?.sales.averageTicket, true)}
        />
        <StatTile
          loading={loading}
          busy={isFetching && !isPending}
          tone="warn"
          label={t('dashboard.costOfGoods')}
          value={formatCurrency(data?.sales.grossSalesCost.value)}
          tooltip={t('dashboard.costOfGoodsHelp')}
          sub={revenueSub(data?.sales.grossSalesCost, true)}
        />
      </StatGroup>

      <section className="space-y-2">
        <SectionTitle
          title={t('dashboard.dailyRevenue')}
          help={t('dashboard.dailyRevenueHelp')}
          actions={
            <Button
              variant="outline"
              size="sm"
              disabled={loading || isFetching}
              onClick={() => setDailyOpen(true)}
            >
              {t('dashboard.viewDetails')}
              <ArrowRight className="size-4" />
            </Button>
          }
        />
        {loading ? (
          // `bg-card` is the page's own card colour, so the old placeholder
          // pulsed an empty card and read as a rendering bug rather than a wait.
          <div className="bg-card space-y-3 rounded-xl border p-3">
            <Skeleton className="h-52 w-full" />
            <div className="flex gap-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ) : (
          <RevenueLineChart
            points={data!.series}
            previousRangeLabel={previousRangeLabel}
            busy={isFetching && !isPending}
          />
        )}

        {/* The Revenue tiles, one row per day of the chart — the same six
            figures, so a day that looks off on the line can be read in full. */}
        <Sheet open={dailyOpen} onOpenChange={setDailyOpen}>
          <SheetContent
            side={isCompact ? 'bottom' : 'right'}
            className={cn(
              'gap-0 overflow-y-auto p-0',
              isCompact ? 'max-h-[85svh] rounded-t-xl' : 'w-full sm:max-w-4xl'
            )}
          >
            <SheetHeader className="border-b pr-12">
              <SheetTitle>{t('dashboard.dailyDetailsTitle')}</SheetTitle>
            </SheetHeader>
            <div className="p-4">
              <SortableTable
                rows={data?.series ?? []}
                columns={dailyColumns(t)}
                rowKey={(row) => row.date}
                busy={isFetching}
              />
            </div>
          </SheetContent>
        </Sheet>
      </section>

      <section className="space-y-2">
        <Tabs
          value={paymentView}
          onValueChange={(v) => setPaymentView(v as typeof paymentView)}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <SectionTitle
              title={t('dashboard.paymentParticipation')}
              help={t('dashboard.paymentParticipationHelp')}
            />
            <TabsList className="sm:w-auto">
              <TabsTrigger
                value="tenderTypes"
                disabled={isFetching}
                className="sm:flex-none sm:px-4"
              >
                {t('dashboard.tenderType')}
              </TabsTrigger>
              <TabsTrigger
                value="cardTypes"
                disabled={isFetching}
                className="sm:flex-none sm:px-4"
              >
                {t('dashboard.cardType')}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="tenderTypes" className="mt-2 space-y-3">
            {!loading && (
              <div className="relative">
                <BusyOverlay show={isFetching && !isPending} size="sm" />
                <ShareBar
                  rows={data?.tenderTypes ?? []}
                  getId={(row) => row.id}
                  getName={(row) => row.name}
                  getValue={(row) => row.amountCollected.value}
                  valueLabel={t('dashboard.amountCollected')}
                  className={stale(isFetching, isPending)}
                />
              </div>
            )}
            <DashboardQuickView
              title={t('dashboard.tenderType')}
              rows={data?.tenderTypes ?? []}
              loading={loading}
              busy={isFetching && !isPending}
              getId={(row) => row.id}
              getName={(row) => row.name}
              getValue={(row) => row.amountCollected.value}
              getShare={(row) => row.share}
              nameLabel={t('dashboard.tenderType')}
              valueLabel={t('dashboard.amountCollected')}
              columns={paymentColumns(t)}
            />
          </TabsContent>
          <TabsContent value="cardTypes" className="mt-2 space-y-3">
            {!loading && (
              <div className="relative">
                <BusyOverlay show={isFetching && !isPending} size="sm" />
                <ShareBar
                  rows={data?.cardTypes ?? []}
                  getId={(row) => row.id}
                  getName={(row) => row.name}
                  getValue={(row) => row.amountCollected.value}
                  valueLabel={t('dashboard.amountCollected')}
                  className={stale(isFetching, isPending)}
                />
              </div>
            )}
            <DashboardQuickView
              title={t('dashboard.cardType')}
              rows={data?.cardTypes ?? []}
              loading={loading}
              busy={isFetching && !isPending}
              getId={(row) => row.id}
              getName={(row) => row.name}
              getValue={(row) => row.amountCollected.value}
              getShare={(row) => row.share}
              nameLabel={t('dashboard.cardType')}
              valueLabel={t('dashboard.amountCollected')}
              columns={paymentColumns(t)}
            />
          </TabsContent>
        </Tabs>
      </section>

      <section className="space-y-2">
        <Tabs value={grouping} onValueChange={(v) => setGrouping(v as Grouping)}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <SectionTitle
              title={t('dashboard.whereItSold')}
              help={t('dashboard.whereItSoldHelp')}
            />
            <TabsList className="sm:w-auto">
              {GROUPINGS.map((g) => (
                <TabsTrigger
                  key={g}
                  value={g}
                  disabled={groupingFetching}
                  className="sm:flex-none sm:px-4"
                >
                  {t(GROUPING_LABEL_KEY[g])}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value={grouping} className="mt-2 space-y-3">
            {!groupingPending && (
              <p
                className="text-muted-foreground text-sm"
                aria-live="polite"
              >
                {t('dashboard.groupingTotal', {
                  value: formatCurrency(groupingTotal),
                })}
              </p>
            )}
            {!groupingPending && (
              <div className="relative">
                <BusyOverlay show={groupingFetching && !groupingPending} size="sm" />
                <ShareBar
                  rows={groupingRows ?? []}
                  getId={(row) => row.id}
                  getName={(row) => row.name}
                  getValue={(row) => row.salesTotal.value}
                  valueLabel={t('dashboard.salesTotal')}
                  className={stale(groupingFetching, groupingPending)}
                />
              </div>
            )}
            <DashboardQuickView
              title={t(GROUPING_LABEL_KEY[grouping])}
              rows={groupingRows ?? []}
              loading={groupingPending}
              busy={groupingFetching && !groupingPending}
              getId={(row) => row.id}
              getName={(row) => row.name}
              getValue={(row) => row.salesTotal.value}
              getShare={(row) => row.share}
              nameLabel={t(GROUPING_LABEL_KEY[grouping])}
              valueLabel={t('dashboard.salesTotal')}
              columns={groupingColumns(t)}
            />
          </TabsContent>
        </Tabs>
      </section>

      <section className="space-y-2">
        <SectionTitle
          title={t('dashboard.topProducts')}
          help={t('dashboard.topProductsHelp')}
        />
        <DashboardQuickView
          title={t('dashboard.topProducts')}
          rows={data?.topProducts ?? []}
          loading={loading}
          busy={isFetching && !isPending}
          getId={(row) => row.id}
          getName={(row) => row.name}
          getValue={(row) => row.salesTotal.value}
          getShare={(row) => row.share}
          nameLabel={t('dashboard.product')}
          valueLabel={t('dashboard.salesTotal')}
          columns={productColumns(t)}
          // Paged and searched against the endpoint rather than over the
          // twenty-five rows behind the summary above it.
          detail={<TopProductsDetail query={query} />}
        />
      </section>

      <section className="space-y-2">
        <SectionTitle title={t('dashboard.loss')} help={t('dashboard.lossHelp')} />
        <DashboardQuickView
          title={t('dashboard.loss')}
          rows={data?.loss ?? []}
          loading={loading}
          busy={isFetching && !isPending}
          getId={(row) => row.id}
          getName={(row) => t(row.reasonKey)}
          getValue={(row) => row.units.value}
          getShare={(row) => row.share}
          isMoney={false}
          nameLabel={t('dashboard.reason')}
          valueLabel={t('dashboard.units')}
          columns={lossColumns(t)}
        />
      </section>
    </div>
  );
}
